import { useEffect, useRef } from 'react';
import { buildTextOffsetMap } from '../../lib/textOffsetMap';

/**
 * Renders existing TextSelection records as inline <mark> elements inside
 * a rendered chapter HTML tree.
 *
 * Runs as an effect after the container's HTML is in the DOM. For each
 * selection it wraps the text range with a <mark> styled per kind/color.
 *
 * We intentionally don't trust an externally-provided offsetMap: each paint
 * pass unwraps prior marks (via parent.normalize()), which merges text nodes
 * and invalidates any cached node references. We rebuild against the live
 * DOM right before painting so offsetsToRange always produces a valid range.
 *
 * Props:
 *   containerRef: React ref to the element whose text nodes host the chapter.
 *   offsetMap:    present only to force a re-run when the reader rebuilds it
 *                 (e.g. after the HTML changes). Not used for coordinate math.
 *   selections:   array of TextSelection-shaped objects (real or synthetic).
 *   onSelectionClick: optional click handler receiving the clicked selection.
 */
const HIGHLIGHT_CLASSES = {
  neutral: 'bg-stone-200 dark:bg-stone-700/60',
  amber:   'bg-amber-100 dark:bg-amber-900/40',
  rose:    'bg-rose-100 dark:bg-rose-900/40',
  sky:     'bg-sky-100 dark:bg-sky-900/40',
};

export default function SelectionRenderer({
  containerRef,
  offsetMap,
  selections,
  onSelectionClick,
  onSelectionHover,
}) {
  // Keep the callbacks in refs so this paint effect does NOT re-run every time
  // the parent passes new function identities. On a Live Session page the
  // parent re-renders constantly (cursor moves, presence), and re-running this
  // effect calls parent.normalize() — which merges text nodes and DESTROYS the
  // teacher's in-progress selection mid-drag, so highlights never land. The
  // marks should only repaint when the selections or the DOM actually change.
  const clickRef = useRef(onSelectionClick);
  const hoverRef = useRef(onSelectionHover);
  useEffect(() => { clickRef.current = onSelectionClick; }, [onSelectionClick]);
  useEffect(() => { hoverRef.current = onSelectionHover; }, [onSelectionHover]);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !selections) return;
    const onSelectionClickStable = (sel, e) => clickRef.current?.(sel, e);
    const onSelectionHoverStable = (sel, rect, isEnter) => hoverRef.current?.(sel, rect, isEnter);

    // Cleanup stale marks from a previous render so painting starts from a
    // known-clean tree. The external `offsetMap` prop is only a render trigger
    // — we don't use its coordinates (they'd be stale post-normalize).
    unwrapAllMarks(container);

    // Highlights that fall entirely within an analyzed sentence's text are
    // visually suppressed — the blue underline is the sole indicator that
    // the sentence was analyzed. The highlight's TextSelection record still
    // lives in the DB; we just don't paint its background.
    const analyzedTexts = selections
      .filter((s) => s.kind === 'analyzed_sentence')
      .map((s) => (s.selected_text || '').trim())
      .filter(Boolean);

    const suppressedByAnalysis = (sel) => {
      if (sel.kind !== 'highlight') return false;
      const t = (sel.selected_text || '').trim();
      if (!t) return false;
      return analyzedTexts.some((at) => at.includes(t));
    };

    // Paint widest first so nesting works (a short highlight inside a long
    // analyzed-sentence underline).
    const ordered = [...selections]
      .filter((sel) => !suppressedByAnalysis(sel))
      .sort((a, b) => {
        const la = (a.text_end ?? 0) - (a.text_start ?? 0);
        const lb = (b.text_end ?? 0) - (b.text_start ?? 0);
        return lb - la;
      });

    // IMPORTANT: rebuild the offset map BEFORE each paint. Each paintSelection
    // splits the text node it wraps, which invalidates node references in any
    // map built earlier. Rebuilding keeps per-paint coordinate math honest.
    // One bad range shouldn't crash the whole reader — skip with a warning.
    const isDev = import.meta.env?.DEV;
    if (isDev) {
      console.log('[SelectionRenderer] paint pass:', ordered.length, 'selections —',
        ordered.map((s) => `${s.kind}#${s.id}`).join(', '));
    }
    ordered.forEach((sel) => {
      try {
        const freshMap = buildTextOffsetMap(container);
        const painted = paintSelection(container, freshMap, sel, onSelectionClickStable, onSelectionHoverStable);
        if (isDev && !painted) {
          console.warn('[SelectionRenderer] paint returned false for', {
            id: sel.id, kind: sel.kind, text_start: sel.text_start, text_end: sel.text_end,
            selected_text: (sel.selected_text || '').slice(0, 40),
            plainTextLen: freshMap.plainText.length,
          });
        }
      } catch (err) {
        console.warn('[SelectionRenderer] failed to paint selection', sel?.id, err);
      }
    });

    return () => {
      try {
        unwrapAllMarks(container);
      } catch (err) {
        console.warn('[SelectionRenderer] cleanup failed', err);
      }
    };
  }, [containerRef, offsetMap, selections]);

  return null;
}

// Unwrap every mark we've previously painted and normalize the parents so
// the offset map built next time sees a clean text-node tree.
function unwrapAllMarks(container) {
  const marks = container.querySelectorAll('mark[data-selection-id]');
  marks.forEach((m) => {
    const p = m.parentNode;
    if (!p) return;
    while (m.firstChild) p.insertBefore(m.firstChild, m);
    p.removeChild(m);
    p.normalize?.();
  });
}

function paintSelection(container, offsetMap, sel, onClick, onHover) {
  // Analyzed-sentence offsets are computed server-side against chapter.full_text,
  // which can differ in whitespace from the DOM's plain text; trust the
  // sentence text itself and locate it in the live DOM instead. User-created
  // selections (highlights/comments/close_reading) were captured client-side
  // so their offsets map cleanly onto the same DOM plainText.
  let range = null;
  if (sel.kind === 'analyzed_sentence' && sel.selected_text) {
    range = findRangeByText(offsetMap, sel.selected_text);
  }
  if (!range) {
    range = offsetMap.offsetsToRange(sel.text_start, sel.text_end);
  }
  if (!range && sel.selected_text) {
    range = findRangeByText(offsetMap, sel.selected_text);
  }
  if (!range) return false;

  const klass = classForSelection(sel);
  const mark = buildMark(sel, klass, onClick, onHover);

  try {
    range.surroundContents(mark);
  } catch {
    // surroundContents throws when the range spans across element boundaries
    // (block or inline). Fall back to paintCrossBlockRange which handles
    // both — cross-block via per-block sub-ranges, inline via extract+insert
    // scoped to a single block.
    paintCrossBlockRange(container, offsetMap, range, sel, klass, onClick, onHover);
  }
  return true;
}

// ── Cross-block painting ────────────────────────────────────────

const BLOCK_TAGS = new Set([
  'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'DIV',
  'BLOCKQUOTE', 'LI', 'FIGURE', 'PRE', 'TABLE', 'SECTION', 'ARTICLE',
]);

function isBlockElement(node) {
  return node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(node.tagName);
}

// Walk up from a text node to the nearest block-level ancestor that is a
// child (direct or nested) of `container`.
function nearestBlock(node, container) {
  let cur = node;
  while (cur && cur !== container) {
    if (isBlockElement(cur)) return cur;
    cur = cur.parentNode;
  }
  return container;
}

// Fallback when range.surroundContents throws. Two distinct failure modes:
//
//   (a) The range crosses BLOCK boundaries (middle of one <p> to middle of
//       the next). extractContents here would rip paragraphs apart, so we
//       split the range into per-block sub-ranges and wrap each.
//
//   (b) The range crosses only INLINE element boundaries within a single
//       block (e.g. "…the uniform of the Guardians…" where "uniform" is
//       inside an <em>). extractContents is safe within one block — it
//       preserves paragraph structure — so we fall back to it.
function paintCrossBlockRange(container, _offsetMap, range, sel, klass, onClick, onHover) {
  const startBlock = nearestBlock(range.startContainer, container);
  const endBlock   = nearestBlock(range.endContainer, container);

  if (startBlock === endBlock) {
    // Case (b): same block, inline-element boundary. extract+wrap+insert.
    try {
      const mark = buildMark(sel, klass, onClick, onHover);
      const contents = range.extractContents();
      mark.appendChild(contents);
      range.insertNode(mark);
    } catch (err) {
      console.warn('[SelectionRenderer] in-block extract fallback failed', sel?.id, err);
    }
    return;
  }

  // Collect block elements between startBlock and endBlock (inclusive) in
  // document order. Use a flat walk of container's children if the blocks
  // are direct children; otherwise fall back to a TreeWalker.
  const blocks = [];
  let collecting = false;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) =>
      isBlockElement(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP,
  });
  let node = walker.nextNode();
  while (node) {
    if (node === startBlock) collecting = true;
    if (collecting) blocks.push(node);
    if (node === endBlock) break;
    node = walker.nextNode();
  }
  if (blocks.length === 0) return;

  blocks.forEach((block, idx) => {
    try {
      const subRange = document.createRange();
      if (idx === 0) {
        // First block: from the original start point to end of block's text.
        subRange.setStart(range.startContainer, range.startOffset);
        subRange.setEnd(block, block.childNodes.length);
      } else if (idx === blocks.length - 1) {
        // Last block: from start of block's text to the original end point.
        subRange.setStart(block, 0);
        subRange.setEnd(range.endContainer, range.endOffset);
      } else {
        // Middle blocks: wrap the entire block's contents.
        subRange.selectNodeContents(block);
      }

      if (subRange.collapsed) return;

      const subMark = buildMark(sel, klass, onClick, onHover);
      try {
        subRange.surroundContents(subMark);
      } catch {
        // Inline-element boundary inside this block too — fall back to
        // extract+insert for this one block (still safe since subRange is
        // clipped to a single block's descendants).
        const contents = subRange.extractContents();
        subMark.appendChild(contents);
        subRange.insertNode(subMark);
      }
    } catch (err) {
      console.warn('[SelectionRenderer] cross-block sub-range failed for block', idx, err);
    }
  });
}

// Build a <mark> element with the selection's attrs, class, and event
// listeners. Shared by the primary paint path + both cross-block branches.
function buildMark(sel, klass, onClick, onHover) {
  const mark = document.createElement('mark');
  mark.setAttribute('data-selection-id', String(sel.id));
  mark.setAttribute('data-selection-kind', sel.kind);
  if (sel.color) mark.setAttribute('data-selection-color', sel.color);
  mark.className = klass;
  mark.style.cursor = 'pointer';
  if (onClick) {
    mark.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick(sel, e);
    });
  }
  if (onHover) {
    mark.addEventListener('mouseenter', () => {
      onHover(sel, mark.getBoundingClientRect(), true);
    });
    mark.addEventListener('mouseleave', () => {
      onHover(sel, mark.getBoundingClientRect(), false);
    });
  }
  return mark;
}

function classForSelection(sel) {
  // text-inherit overrides the browser's default <mark> text color (black),
  // which otherwise produces black-on-dark in dark mode.
  const base = 'text-inherit';
  if (sel.kind === 'highlight') {
    return `${base} ${HIGHLIGHT_CLASSES[sel.color] || HIGHLIGHT_CLASSES.neutral} px-0.5 rounded`;
  }
  if (sel.kind === 'comment') {
    return `${base} bg-transparent underline decoration-stone-400 decoration-dotted underline-offset-4 cursor-pointer`;
  }
  if (sel.kind === 'close_reading') {
    return `${base} bg-transparent border-l-2 border-stone-400 dark:border-stone-500 pl-2 cursor-pointer`;
  }
  if (sel.kind === 'analyzed_sentence') {
    return `${base} bg-transparent underline decoration-blue-500 decoration-2 underline-offset-4 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors cursor-pointer`;
  }
  return base;
}

// Fallback: scan plainText for the first occurrence of needle and convert
// back to a Range.
function findRangeByText(offsetMap, needle) {
  const idx = offsetMap.plainText.indexOf(needle);
  if (idx < 0) return null;
  return offsetMap.offsetsToRange(idx, idx + needle.length);
}
